
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, Search, MoreVertical, Bell, User, Menu, Home } from 'lucide-react';
import { useMobile } from './MobileOptimized';
import { useAuth } from '../../hooks/useAuth';
import { cn } from '@/lib/utils';
import { ModeToggle } from '@/components/ModeToggle';

const MobileHeader = ({
  title,
  showBack = false,
  showSearch = false,
  showNotifications = true,
  showUserMenu = true,
  actions = []
}) => {
  const { isMobile, isTouchDevice } = useMobile();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hasNotifications, setHasNotifications] = useState(false);
  const navigationHistory = useRef([]);
  const previousPathname = useRef(null);

  if (!isMobile) return null;

  // Track navigation history within the app for smart back navigation
  useEffect(() => {
    const currentPath = location.pathname;

    // Don't track if it's the same page (e.g., param changes)
    if (previousPathname.current === currentPath) {
      return;
    }

    // Add to history if it's a new navigation
    if (previousPathname.current) {
      navigationHistory.current.push(previousPathname.current);
      // Keep only last 10 pages to prevent memory bloat
      if (navigationHistory.current.length > 10) {
        navigationHistory.current.shift();
      }
    }

    previousPathname.current = currentPath;
  }, [location.pathname]);

  // Determine if we should show back button (all pages except dashboard and auth)
  const isLandingOrAuthPage =
    location.pathname === '/' ||
    location.pathname.startsWith('/auth') ||
    location.pathname.startsWith('/super-admin') ||
    location.pathname === '/pricing' ||
    location.pathname === '/features' ||
    location.pathname === '/about' ||
    location.pathname === '/contact' ||
    location.pathname === '/privacy' ||
    location.pathname === '/terms' ||
    location.pathname === '/cookies' ||
    location.pathname === '/gdpr';

  const isDashboard = location.pathname === '/dashboard';
  const shouldShowBackButton = !isLandingOrAuthPage && !isDashboard && showBack !== false;

  const handleBack = () => {
    // Try to use app's navigation history first
    if (navigationHistory.current.length > 0) {
      const previousPage = navigationHistory.current.pop();
      navigate(previousPage);
      return;
    }

    // Fall back to browser history if it exists
    if (window.history.length > 2) {
      navigate(-1);
      return;
    }

    // If no history (deep link), navigate to dashboard
    navigate('/dashboard');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const getPageTitle = () => {
    if (title) return title;

    const path = location.pathname;
    // Main sections
    if (path === '/dashboard') return 'Dashboard';
    if (path === '/invoices') return 'Invoices';
    if (path === '/customers') return 'Customers';
    if (path === '/analytics') return 'Analytics';
    if (path === '/settings') return 'Settings';
    if (path === '/payments') return 'Payments';
    if (path === '/documents') return 'Documents';

    // Admin sections
    if (path === '/admin') return 'Admin';
    if (path === '/super-admin') return 'Super Admin';
    if (path === '/tenant-management') return 'Tenants';
    if (path === '/platform-config') return 'Platform Config';
    if (path === '/user-management') return 'Users';
    if (path === '/security') return 'Security';
    if (path === '/billing') return 'Billing';

    // Other pages
    if (path === '/onboarding') return 'Onboarding';
    if (path === '/agent-dashboard') return 'AI Agents';

    return 'Wixell';
  };

  // Get breadcrumb path for current page
  const getBreadcrumb = () => {
    const path = location.pathname;
    const segments = path.split('/').filter(Boolean);

    if (segments.length <= 1) return null;

    // For nested routes, show parent > current
    const breadcrumbMap = {
      'invoice-settings': 'Invoices > Settings',
      'billing': 'Settings > Billing',
      'security': 'Settings > Security',
      'platform-config': 'Admin > Config',
      'tenant-management': 'Admin > Tenants',
      'user-management': 'Admin > Users'
    };

    const lastSegment = segments[segments.length - 1];
    return breadcrumbMap[lastSegment] || null;
  };

  const handleSearchToggle = () => {
    setIsSearchExpanded(!isSearchExpanded);
  };

  const breadcrumb = getBreadcrumb();

  return (
    <header className="sticky top-0 z-40 mobile-safe-area bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="flex items-center justify-between h-14 px-4">
        {/* Left side - Back button, Home indicator, or Menu */}
        <div className="flex items-center min-w-0 flex-1">
          {shouldShowBackButton ? (
            // Show back button on nested pages
            <Button
              variant="ghost"
              size="sm"
              className="mr-2 h-11 w-11 p-0 touch-target touch-feedback shrink-0"
              onClick={handleBack}
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          ) : isDashboard ? (
            // Show home indicator on dashboard
            <div
              className="mr-2 h-11 w-11 p-0 flex items-center justify-center shrink-0"
              aria-label="Dashboard home"
            >
              <Home className="h-5 w-5 text-primary" />
            </div>
          ) : (
            // Show menu button on landing/auth pages
            <Button
              variant="ghost"
              size="sm"
              className="mr-2 h-11 w-11 p-0 touch-target touch-feedback shrink-0 lg:hidden"
              onClick={toggleMobileMenu}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}

          {/* Page Title and Breadcrumb */}
          <div className="flex flex-col justify-center min-w-0 flex-1">
            {breadcrumb && (
              <p className="text-xs text-muted-foreground truncate leading-tight">
                {breadcrumb}
              </p>
            )}
            <h1 className={cn(
              "font-semibold truncate",
              breadcrumb ? "text-base leading-tight" : "text-lg"
            )}>
              {getPageTitle()}
            </h1>
          </div>
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-0.5 shrink-0">
          {/* Search */}
          {showSearch && (
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-11 w-11 p-0 touch-target touch-feedback",
                isSearchExpanded && "text-primary"
              )}
              onClick={handleSearchToggle}
              aria-label="Toggle search"
              aria-pressed={isSearchExpanded}
            >
              <Search className="h-5 w-5" />
            </Button>
          )}

          {/* Theme Toggle - wrapped to ensure proper touch target */}
          <div className="h-11 w-11 flex items-center justify-center">
            <ModeToggle />
          </div>

          {/* Notifications */}
          {showNotifications && (
            <Button
              variant="ghost"
              size="sm"
              className="h-11 w-11 p-0 touch-target touch-feedback relative"
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5" />
              {/* Notification badge - show only if there are notifications */}
              {hasNotifications && (
                <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-destructive rounded-full animate-pulse"
                      aria-label="You have unread notifications" />
              )}
            </Button>
          )}

          {/* User menu */}
          {showUserMenu && (
            <Button
              variant="ghost"
              size="sm"
              className="h-11 w-11 p-0 touch-target touch-feedback"
              aria-label="User menu"
            >
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name || 'User'}
                  className="h-6 w-6 rounded-full object-cover"
                />
              ) : (
                <User className="h-5 w-5" />
              )}
            </Button>
          )}

          {/* Custom actions */}
          {actions.map((action, index) => (
            <Button
              key={index}
              variant="ghost"
              size="sm"
              className="h-11 w-11 p-0 touch-target touch-feedback"
              onClick={action.onClick}
              aria-label={action.label || `Action ${index + 1}`}
            >
              <action.icon className="h-5 w-5" />
            </Button>
          ))}

          {/* More menu - only if no custom actions */}
          {actions.length === 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-11 w-11 p-0 touch-target touch-feedback"
              aria-label="More options"
            >
              <MoreVertical className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Expandable search bar */}
      {showSearch && isSearchExpanded && (
        <div className="px-4 pb-3 mobile-slide-up">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="search"
              placeholder="Search..."
              className={cn(
                "w-full h-10 pl-10 pr-4 rounded-lg border border-input bg-background",
                "text-sm placeholder:text-muted-foreground",
                "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                "transition-all duration-200"
              )}
              autoFocus
              onBlur={() => setIsSearchExpanded(false)}
            />
          </div>
        </div>
      )}
    </header>
  );
};

export default MobileHeader;
